/**
 * Opening a generated document (a PDF blob) in a new tab, or saving it.
 *
 * ── WHY THIS IS A MODULE AND NOT TWO COPIES ─────────────────────────────────
 *
 * Chrome grants a document TRANSIENT USER ACTIVATION for five seconds after a
 * click. `window.open` called outside that window returns `null` and opens
 * nothing — it does NOT throw, so a `try/catch` around it catches nothing and
 * the caller's error branch never runs. The button simply does nothing, with no
 * console error, no failed request and no toast.
 *
 * Both document screens had the same shape:
 *
 *     const res = await api.…getPdf(id);      // ~2.6s, measured
 *     window.open(URL.createObjectURL(res.data), '_blank');   // past the budget
 *
 * Rendering a ticket PDF measures ~2.6s against a 5s budget BEFORE the user's
 * own network is added, so the failure is latency-dependent: fine on a fast
 * connection, permanent on a slow one, intermittent in between. It reached a
 * pilot tenant's office as "we've lost the ability to preview an invoice"
 * (fe#763).
 *
 * The fix is ordering, not error handling: claim the tab synchronously while
 * the gesture is still live, then point it at the blob when the bytes arrive.
 *
 * ── THE NULL IS STILL REAL AFTER THE FIX, AND MEANS SOMETHING DIFFERENT ──────
 *
 * A synchronous `window.open` can still be refused — the user may have blocked
 * pop-ups for the site outright. That null is a genuine, actionable refusal and
 * must be SAID. Before the fix a null meant "we asked too late" (our bug, and
 * unreportable); after it, a null means "your browser refused" (the user's
 * setting, and fixable by them). Never let either pass as success.
 */

/**
 * How long a blob URL stays alive after it is handed to the browser.
 *
 * NOT revoked on the next statement. `window.open` and `a.click()` both merely
 * SCHEDULE work — the tab is still loading, and the download has not read a
 * byte, when the following line runs. Revoking there invalidates the URL under
 * them: the tab lands on a blank page and the download saves nothing or fails.
 * It is a race, so it survives every test and shows up on a slow disk or a
 * large document.
 *
 * A minute is far longer than either needs and costs one unreferenced blob for
 * that long. The failure it prevents is silent; the cost it carries is not.
 */
export const BLOB_URL_TTL_MS = 60_000;

/**
 * What to tell the user when the browser refuses the tab. Names the setting to
 * change AND an action that works without changing it — a message that only
 * says "blocked" leaves someone who cannot change that setting with nowhere to
 * go.
 */
export const POPUP_BLOCKED_MESSAGE =
  'Your browser blocked the PDF tab. Allow pop-ups for this site, or use Save as PDF instead.';

/**
 * Claim a blank tab. MUST be called synchronously from the click handler,
 * before any `await` — that is the whole point of this module.
 *
 * Returns `null` when the browser refused, which the caller must report rather
 * than proceed through.
 */
export function claimBlobTab(): Window | null {
  return window.open('', '_blank');
}

/** Point an already-claimed tab at a blob, and schedule the URL's cleanup. */
export function showBlobInTab(win: Window, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  win.location.href = url;
  setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
}

/**
 * Save a blob under a chosen filename.
 *
 * Not subject to the activation rule — an anchor click is not a pop-up — but it
 * IS subject to the revoke race above, which is why it lives here beside the
 * constant rather than being inlined at each call site.
 */
export function saveBlobAs(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_TTL_MS);
}

// ─── Printing ────────────────────────────────────────────────────────────────
//
// PRINT FAILED ON BOTH OF A PILOT TENANT'S DEVICES (a Windows PC and an Android
// tablet, both Chrome — 2026-09-27), for three separate reasons:
//
// 1. The document menu sat UNDER the band's stat strip, so a click on "Print"
//    landed on a stat figure and no handler ran. That is a CSS fix
//    (PageHeaderBand.css), not this module, but it is why the other two were
//    never seen.
// 2. Desktop: the frame was `display: none`, `print()` fired on its `load` (11ms
//    after append, measured — before the PDF viewer had anything to print) and
//    the frame was removed a second later, tearing down the print preview it
//    had just opened.
// 3. Android and iOS: the browser has no inline PDF viewer to print from. A
//    hidden frame there prints the page around it, or nothing. The only thing
//    that prints a PDF on those devices is the browser's own viewer in a tab.

/** Toast shown after Print hands the PDF to a tab on a phone or tablet. */
export const PRINT_IN_TAB_MESSAGE = "Opened — use your browser's print";

/** Shown when the desktop print frame never loads or refuses `print()`. */
export const PRINT_FAILED_MESSAGE =
  "Couldn't open the print dialog. Use View PDF and print from the tab instead.";

/**
 * How long a print frame may take to load before we say it failed. A blob is
 * local, so this is only ever reached when the browser will not load it.
 */
export const PRINT_LOAD_TIMEOUT_MS = 15_000;

/**
 * Whether Print must go through a tab rather than a hidden frame. True on
 * Android (phone and tablet — Android tablets omit "Mobile" but keep
 * "Android"), iPhone, iPad, and iPadOS, which reports itself as a Mac with a
 * touch screen.
 */
export function printsViaTab(nav: Navigator = navigator): boolean {
  const ua = nav.userAgent || '';
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
  return /Macintosh/.test(ua) && (nav.maxTouchPoints ?? 0) > 1;
}

/**
 * The one print frame on the page. Kept until the NEXT print (or
 * `disposePrintFrame`), never on a timer: the print dialog belongs to the
 * frame's document, and removing the frame while the dialog is open closes it.
 * An unseen frame left on the page costs nothing; a dialog that vanishes
 * under the user costs the print.
 */
let printFrame: { frame: HTMLIFrameElement; url: string } | null = null;

export function disposePrintFrame(): void {
  if (!printFrame) return;
  printFrame.frame.remove();
  URL.revokeObjectURL(printFrame.url);
  printFrame = null;
}

/**
 * Print a PDF blob through an unseen frame (desktop only — see
 * `printsViaTab`). Resolves once `print()` has been called; rejects if the
 * frame does not load or `print()` throws, so the caller can SAY so.
 *
 * NOT `display: none`: a frame that is not rendered gets no PDF viewer, so
 * there is nothing to print. One pixel, transparent and off the click path
 * keeps it rendered and unseen.
 */
export function printBlobInFrame(blob: Blob): Promise<void> {
  disposePrintFrame();
  const url = URL.createObjectURL(blob);
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('data-testid', 'pdf-print-frame');
  frame.tabIndex = -1;
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none;';
  printFrame = { frame, url };

  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      disposePrintFrame();
      reject(new Error('print frame did not load'));
    }, PRINT_LOAD_TIMEOUT_MS);

    frame.onload = () => {
      clearTimeout(timer);
      try {
        const win = frame.contentWindow;
        if (!win) throw new Error('print frame has no window');
        win.focus();
        win.print();
        resolve();
      } catch (err) {
        disposePrintFrame();
        reject(err);
      }
    };
    frame.src = url;
    document.body.appendChild(frame);
  });
}
